import { Video, VideoSlash } from 'iconsax-reactjs';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/hooks/usePermission';
import { call } from '@/services/call';
import { logger } from '@/services/logger';
import { selectSettingsPermissionsCamera } from '@/store/features/settings/selectors';
import { selectUserVideoEnabled } from '@/store/features/user/selectors';
import { toggleLocalVideoEnabled } from '@/store/features/user/slice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { ToggleButton } from '@/ui';
import { showErrorToast } from '@/utils/toast';

const VideoToggleButton = () => {
  usePermission('camera');

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const enabled = useAppSelector(selectUserVideoEnabled);
  const permission = useAppSelector(selectSettingsPermissionsCamera);

  const isTogglingVideo = useRef(false);

  const toggle = useCallback((value: boolean) => {
    dispatch(toggleLocalVideoEnabled(value));
    return call.toggleVideo(value);
  }, [dispatch]);

  useEffect(() => {
    if (permission !== 'pending' && permission !== 'granted') {
      toggle(false);
    }
  }, [permission, toggle]);

  const onClick = async () => {
    try {
      if (!isTogglingVideo.current) {
        isTogglingVideo.current = true;
        await toggle(!enabled);
      }
    } catch (error) {
      showErrorToast(t('error'), t('something_went_wrong'));
      logger.error(error);
    } finally {
      isTogglingVideo.current = false;
    }
  };

  return (
    <ToggleButton disabled={permission !== 'granted'} onClick={onClick}>
      {enabled ? <Video /> : <VideoSlash />}
    </ToggleButton>
  );
};

export default memo(VideoToggleButton);
